// Mirayama product page.
// 1. <mm-dn-control>: the "Day · Night" control in the info column. It drives the first gallery
//    frame (<mm-day-night class="mm-dn-pdp">, mm-day-night.js), which crossfades between the
//    day and the night photograph, and brings that frame back into view if it has scrolled away.
// 2. The sticky info column on tablet and desktop: when it is taller than the screen (folds
//    open), it scrolls with the page until its end is in view and then stays, so nothing in it
//    is ever out of reach.
// 3. <mm-buy-bar>: the phone buy bar (P-01), snippet mm-buy-bar.
// 4. <mm-zoom>: the full-screen image viewer of the gallery (P-05), snippet mm-product-gallery.
// 5. Gallery helpers: the phone counter is announced once the row settles (P-17), and films that
//    wait with preload="none" start loading one swipe / one screen ahead (P-16).

if (!customElements.get('mm-dn-control')) {
  customElements.define(
    'mm-dn-control',
    class MmDnControl extends HTMLElement {
      connectedCallback() {
        const scope = this.closest('product-info') || document;
        this.target = scope.querySelector('mm-day-night.mm-dn-pdp');
        if (!this.target) {
          this.hidden = true;
          return;
        }
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.buttons = Array.from(this.querySelectorAll('[data-dn-set]'));
        this.buttons.forEach((button) => {
          button.addEventListener('click', () => this.choose(button.dataset.dnSet));
          // Start fetching the other photograph as soon as someone reaches for the control.
          button.addEventListener('pointerenter', () => this.warm(), { once: true });
          button.addEventListener('focus', () => this.warm(), { once: true });
        });
      }

      warm() {
        if (typeof this.target.warm === 'function') this.target.warm();
      }

      choose(state) {
        if (typeof this.target.set === 'function') {
          this.target.set(state);
        } else {
          this.target.dataset.state = state;
        }
        this.dataset.state = state;
        this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.dnSet === state)));
        this.reveal();
      }

      // Bring the first frame back: the phone row scrolls to its start, the page scrolls up to it.
      reveal() {
        const behavior = this.reduced ? 'auto' : 'smooth';
        const track = this.target.closest('.mm-gallery__track');
        if (track && track.scrollLeft > 2) track.scrollTo({ left: 0, behavior });
        const rect = this.target.getBoundingClientRect();
        const inView = rect.bottom > 120 && rect.top < window.innerHeight - 120;
        if (!inView) {
          const top = window.scrollY + rect.top - 24;
          window.scrollTo({ top: Math.max(0, top), behavior });
        }
      }
    }
  );
}

(function () {
  const GAP = 30; // Dawn's sticky offset (3rem)
  const tablet = window.matchMedia('(min-width: 750px)');

  function fit(el) {
    if (!tablet.matches) {
      el.style.top = '';
      return;
    }
    const top = Math.min(GAP, window.innerHeight - el.offsetHeight - GAP);
    el.style.top = `${top}px`;
  }

  function init() {
    document.querySelectorAll('.mm-pdp .product__info-container.product__column-sticky').forEach((el) => {
      if (el.dataset.mmFit) return;
      el.dataset.mmFit = 'true';
      const update = () => fit(el);
      new ResizeObserver(update).observe(el);
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


// 3. Phone buy bar ----------------------------------------------------------------------------
if (!customElements.get('mm-buy-bar')) {
  customElements.define(
    'mm-buy-bar',
    class MmBuyBar extends HTMLElement {
      connectedCallback() {
        this.phone = window.matchMedia('(max-width: 749px)');
        const scope = document.getElementById(`MainProduct-${this.dataset.section}`) || document;
        this.main = scope.querySelector('[data-mm-buy]');
        if (!this.main) return;

        this.past = false; // the main button has scrolled out of view above the screen
        this.footerInView = false;
        this.blocked = false; // cart drawer or image viewer open
        this.footer = document.querySelector('.shopify-section-group-footer-group, footer');

        // The observer's area runs from the top of the screen down without end, so "not
        // intersecting" means exactly "above the screen", and a jump straight past the button
        // (anchor, fling) still changes state and fires.
        if ('IntersectionObserver' in window) {
          this.mainObserver = new IntersectionObserver(
            ([entry]) => {
              this.past = !entry.isIntersecting;
              this.update();
            },
            { rootMargin: '0px 0px 100000px 0px' }
          );
          this.mainObserver.observe(this.main);
          if (this.footer) {
            this.footerObserver = new IntersectionObserver(([entry]) => {
              this.footerInView = entry.isIntersecting;
              this.update();
            });
            this.footerObserver.observe(this.footer);
          }
        }

        // Hide while the cart drawer is open (Dawn marks it .active) or the image viewer is open.
        const drawer = document.querySelector('cart-drawer');
        const syncBlocked = () => {
          this.blocked =
            (drawer && drawer.classList.contains('active')) ||
            document.documentElement.classList.contains('mm-zoom-open');
          this.update();
        };
        if (drawer) new MutationObserver(syncBlocked).observe(drawer, { attributes: true, attributeFilter: ['class'] });
        new MutationObserver(syncBlocked).observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['class'],
        });

        const add = this.querySelector('[data-mm-buy-bar-add]');
        if (add) {
          add.addEventListener('click', () => {
            if (this.main.getAttribute('aria-disabled') === 'true' || this.main.disabled) return;
            this.main.click();
          });
        }

        if (this.phone.addEventListener) this.phone.addEventListener('change', () => this.update());
        this.update();
      }

      disconnectedCallback() {
        this.mainObserver?.disconnect();
        this.footerObserver?.disconnect();
      }

      update() {
        const show = this.phone.matches && this.past && !this.footerInView && !this.blocked;
        if (show === this.classList.contains('is-visible')) return;
        this.classList.toggle('is-visible', show);
        this.inert = !show;
        if (!show && this.contains(document.activeElement)) this.main.focus({ preventScroll: true });
      }
    }
  );
}

// 4. Full-screen image viewer -----------------------------------------------------------------
// Opens from any .mm-gallery__zoom button. 2400px source, fitted to the screen.
// Phone: pinch to zoom, drag to pan when zoomed, double-tap to zoom in or out; swipe sideways for
// the next or previous image, swipe up or down to close. Desktop: click to zoom in, move the mouse
// to pan, click again to zoom out; arrow keys for next and previous. Esc closes everywhere.
// Native <dialog> with showModal: the page behind is inert, and Tab is kept inside.
if (!customElements.get('mm-zoom')) {
  customElements.define(
    'mm-zoom',
    class MmZoom extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.stage = this.querySelector('[data-mm-zoom-stage]');
        this.img = this.querySelector('[data-mm-zoom-image]');
        this.count = this.querySelector('[data-mm-zoom-count]');
        this.hint = this.querySelector('[data-mm-zoom-hint]');
        this.gallery = document.querySelector(`[data-mm-zoom-target="${this.id}"]`);
        if (!this.dialog || !this.gallery) return;
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.items = [];
        this.index = 0;
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.pointers = new Map();

        this.gallery.addEventListener('click', (event) => {
          const button = event.target.closest('[data-mm-zoom-open]');
          if (button) this.open(button);
        });
        this.querySelector('[data-mm-zoom-close]').addEventListener('click', () => this.close());
        this.querySelector('[data-mm-zoom-prev]')?.addEventListener('click', () => this.go(-1));
        this.querySelector('[data-mm-zoom-next]')?.addEventListener('click', () => this.go(1));
        this.dialog.addEventListener('close', () => this.cleanup());
        this.dialog.addEventListener('keydown', (event) => this.onKey(event));
        this.img.addEventListener('load', () => this.layout());
        window.addEventListener('resize', () => this.dialog.open && this.layout());

        this.stage.addEventListener('pointerdown', (e) => this.onDown(e));
        this.stage.addEventListener('pointermove', (e) => (e.pointerType === 'mouse' ? this.onMouse(e) : this.onMove(e)));
        this.stage.addEventListener('pointerup', (e) => this.onUp(e));
        this.stage.addEventListener('pointercancel', (e) => this.onUp(e, true));
      }

      // Each zoom button describes its image; the Day · Night frame opens on the photograph shown.
      // The photograph already on the page (preview) shows at once; the 2400px source replaces it.
      collect() {
        return Array.from(this.gallery.querySelectorAll('[data-mm-zoom-open]')).map((b) => {
          const li = b.closest('li');
          const base = li?.querySelector('.mm-gallery__image');
          const state = li?.querySelector('mm-day-night')?.dataset.state;
          const layer = state && li.querySelector(`[data-dn-layer="${state}"]`);
          if (state === 'day' && b.dataset.srcDay) {
            const preview = layer ? layer.currentSrc : base?.currentSrc;
            return { src: b.dataset.srcDay, preview, ratio: +b.dataset.ratioDay, alt: b.dataset.altDay, button: b };
          }
          if (state === 'night' && b.dataset.srcNight) {
            const preview = layer ? layer.currentSrc : base?.currentSrc;
            return { src: b.dataset.srcNight, preview, ratio: +b.dataset.ratioNight, alt: b.dataset.altNight, button: b };
          }
          return { src: b.dataset.src, preview: base?.currentSrc, ratio: +b.dataset.ratio || 0.8, alt: b.dataset.alt, button: b };
        });
      }

      open(button) {
        this.items = this.collect();
        this.opener = button;
        this.index = Math.max(0, this.items.findIndex((item) => item.button === button));
        this.fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        if (this.hint) {
          this.hint.textContent = this.fine ? 'Click to zoom' : 'Pinch to zoom';
        }
        document.documentElement.classList.add('mm-zoom-open');
        this.dialog.showModal();
        this.show();
        this.querySelector('[data-mm-zoom-close]').focus({ preventScroll: true });
      }

      close() {
        if (this.dialog.open) this.dialog.close();
      }

      cleanup() {
        document.documentElement.classList.remove('mm-zoom-open');
        this.pointers.clear();
        this.img.removeAttribute('src');
        if (this.opener) this.opener.focus({ preventScroll: true });
      }

      go(step) {
        if (this.items.length < 2) return;
        this.index = (this.index + step + this.items.length) % this.items.length;
        this.show();
      }

      show() {
        const item = this.items[this.index];
        if (!item) return;
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.dialog.classList.remove('is-zoomed');
        this.img.alt = item.alt || '';
        this.ratio = item.ratio || 0.8;
        this.layout();
        const shown = this.index;
        if (item.preview && !item.loaded) {
          this.img.src = item.preview;
          const full = new Image();
          full.onload = () => {
            item.loaded = true;
            if (this.dialog.open && this.index === shown) this.img.src = item.src;
          };
          full.src = item.src;
        } else {
          this.img.src = item.src;
        }
        this.count.textContent = `Image ${this.index + 1} of ${this.items.length}`;
        // Warm the neighbours so next and previous are instant.
        [1, -1].forEach((step) => {
          const n = this.items[(this.index + step + this.items.length) % this.items.length];
          if (n && n !== item) new Image().src = n.src;
        });
      }

      // Fit the image inside the stage at its own ratio, clear of the counter above and the hint
      // below; zoom and pan are a transform on top and may use the whole screen.
      layout() {
        const W = this.stage.clientWidth;
        const H = this.stage.clientHeight;
        const room = H - 2 * Math.min(64, H * 0.08);
        let w = W;
        let h = W / this.ratio;
        if (h > room) {
          h = room;
          w = room * this.ratio;
        }
        this.w = w;
        this.h = h;
        this.W = W;
        this.H = H;
        this.img.style.width = `${w}px`;
        this.img.style.height = `${h}px`;
        this.apply();
      }

      maxScale() {
        const natural = this.img.naturalWidth || 2400;
        return Math.max(2, Math.min(4, natural / this.w));
      }

      clamp() {
        const mx = Math.max(0, (this.w * this.scale - this.W) / 2);
        const my = Math.max(0, (this.h * this.scale - this.H) / 2);
        this.tx = Math.min(mx, Math.max(-mx, this.tx));
        this.ty = Math.min(my, Math.max(-my, this.ty));
      }

      apply(animate) {
        this.img.style.transition = animate && !this.reduced ? 'transform 0.3s ease' : 'none';
        this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
        this.dialog.classList.toggle('is-zoomed', this.scale > 1.01);
      }

      // Point relative to the stage centre.
      local(e) {
        const r = this.stage.getBoundingClientRect();
        return { x: e.clientX - r.left - r.width / 2, y: e.clientY - r.top - r.height / 2 };
      }

      // Zoom to `scale` keeping the image point under p where it is.
      zoomAt(p, scale, animate) {
        const s0 = this.scale;
        this.tx = p.x - ((p.x - this.tx) / s0) * scale;
        this.ty = p.y - ((p.y - this.ty) / s0) * scale;
        this.scale = scale;
        this.clamp();
        this.apply(animate);
      }

      onKey(event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(-1);
        } else if (event.key === 'Tab') {
          const f = Array.from(this.dialog.querySelectorAll('button')).filter((b) => b.offsetParent !== null);
          if (!f.length) return;
          const first = f[0];
          const last = f[f.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }

      // Desktop: click zooms in at the pointer, or back out; the mouse pans while zoomed.
      onMouse(e) {
        if (this.scale <= 1.01 || this.pointers.size) return;
        const p = this.local(e);
        const mx = Math.max(0, (this.w * this.scale - this.W) / 2);
        const my = Math.max(0, (this.h * this.scale - this.H) / 2);
        this.tx = (-p.x / (this.W / 2)) * mx;
        this.ty = (-p.y / (this.H / 2)) * my;
        this.clamp();
        this.apply();
      }

      onDown(e) {
        if (e.target.closest('button')) return;
        if (e.pointerType === 'mouse') {
          if (e.button !== 0) return;
          const p = this.local(e);
          if (this.scale > 1.01) {
            this.scale = 1;
            this.tx = 0;
            this.ty = 0;
            this.apply(true);
          } else {
            const s = this.maxScale();
            const mx = Math.max(0, (this.w * s - this.W) / 2);
            const my = Math.max(0, (this.h * s - this.H) / 2);
            this.scale = s;
            this.tx = (-p.x / (this.W / 2)) * mx;
            this.ty = (-p.y / (this.H / 2)) * my;
            this.clamp();
            this.apply(true);
          }
          return;
        }
        this.stage.setPointerCapture?.(e.pointerId);
        this.pointers.set(e.pointerId, this.local(e));
        if (this.pointers.size === 1) {
          this.start = { p: this.local(e), tx: this.tx, ty: this.ty, t: Date.now() };
        } else if (this.pointers.size === 2) {
          const [a, b] = Array.from(this.pointers.values());
          this.pinch = {
            d: Math.hypot(a.x - b.x, a.y - b.y) || 1,
            s: this.scale,
            mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
            tx: this.tx,
            ty: this.ty,
          };
          this.start = null;
        }
      }

      onMove(e) {
        if (!this.pointers.has(e.pointerId)) return;
        this.pointers.set(e.pointerId, this.local(e));
        if (this.pointers.size === 2 && this.pinch) {
          const [a, b] = Array.from(this.pointers.values());
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
          const scale = Math.min(this.maxScale(), Math.max(1, (this.pinch.s * d) / this.pinch.d));
          const k = scale / this.pinch.s;
          this.tx = mid.x - (this.pinch.mid.x - this.pinch.tx) * k;
          this.ty = mid.y - (this.pinch.mid.y - this.pinch.ty) * k;
          this.scale = scale;
          this.clamp();
          this.apply();
        } else if (this.pointers.size === 1 && this.start) {
          const p = this.local(e);
          const dx = p.x - this.start.p.x;
          const dy = p.y - this.start.p.y;
          if (this.scale > 1.01) {
            this.tx = this.start.tx + dx;
            this.ty = this.start.ty + dy;
            this.clamp();
            this.apply();
          } else {
            // Follow the finger a little, so the swipe is felt.
            this.img.style.transition = 'none';
            const horizontal = Math.abs(dx) > Math.abs(dy);
            this.img.style.transform = horizontal ? `translateX(${dx * 0.6}px)` : `translateY(${dy * 0.6}px)`;
            this.img.style.opacity = horizontal ? '' : String(Math.max(0.4, 1 - Math.abs(dy) / 600));
          }
        }
      }

      onUp(e, cancelled) {
        if (!this.pointers.has(e.pointerId)) return;
        this.pointers.delete(e.pointerId);
        if (this.pointers.size === 1) {
          // One finger left after a pinch: carry on panning from here.
          const [p] = Array.from(this.pointers.values());
          this.start = { p, tx: this.tx, ty: this.ty, t: Date.now() };
          this.pinch = null;
          return;
        }
        if (this.pointers.size > 0) return;
        this.pinch = null;
        const start = this.start;
        this.start = null;
        this.img.style.opacity = '';
        if (!start || cancelled) {
          this.apply(true);
          return;
        }
        const p = this.local(e);
        const dx = p.x - start.p.x;
        const dy = p.y - start.p.y;
        const quick = Date.now() - start.t < 300;

        if (this.scale <= 1.01) {
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            this.go(dx < 0 ? 1 : -1);
            return;
          }
          if (Math.abs(dy) > 80 && Math.abs(dy) > Math.abs(dx)) {
            this.close();
            return;
          }
        }
        // Double tap: zoom in at the tap, or back out.
        if (quick && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
          const now = Date.now();
          if (this.lastTap && now - this.lastTap < 320) {
            this.lastTap = 0;
            if (this.scale > 1.01) {
              this.scale = 1;
              this.tx = 0;
              this.ty = 0;
              this.apply(true);
            } else {
              this.zoomAt(p, Math.min(2.5, this.maxScale()), true);
            }
            return;
          }
          this.lastTap = now;
        }
        this.apply(true);
      }
    }
  );
}

// 5. Gallery helpers ----------------------------------------------------------------------------
(function () {
  function init() {
    document.querySelectorAll('mm-gallery').forEach((gallery) => {
      if (gallery.dataset.mmHelpers) return;
      gallery.dataset.mmHelpers = 'true';
      const track = gallery.querySelector('.mm-gallery__track');
      if (!track) return;
      const items = Array.from(track.children);
      const phone = window.matchMedia('(max-width: 749px)');

      // Counter for screen readers: "Image 3 of 11", once the row has settled.
      const status = gallery.querySelector('[data-mm-gallery-status]');
      if (status) {
        let timer;
        let last = 0;
        const announce = () => {
          const start = track.getBoundingClientRect().left;
          let index = 0;
          let best = Infinity;
          items.forEach((item, i) => {
            const d = Math.abs(item.getBoundingClientRect().left - start);
            if (d < best) {
              best = d;
              index = i;
            }
          });
          if (index !== last) {
            last = index;
            status.textContent = `Image ${index + 1} of ${items.length}`;
          }
        };
        track.addEventListener(
          'scroll',
          () => {
            clearTimeout(timer);
            timer = setTimeout(announce, 180);
          },
          { passive: true }
        );
      }

      // Films waiting with preload="none": load once one swipe (phone) or one screen (desktop) away.
      const waiting = Array.from(track.querySelectorAll('video')).filter((v) => v.getAttribute('preload') === 'none');
      if (!waiting.length || !('IntersectionObserver' in window)) return;
      const warm = (video) => {
        if (video.dataset.mmWarm) return;
        video.dataset.mmWarm = 'true';
        video.preload = 'auto';
        if (video.readyState === 0 && video.networkState !== HTMLMediaElement.NETWORK_LOADING) video.load();
      };
      let observer;
      const watch = () => {
        observer?.disconnect();
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              warm(entry.target);
              observer.unobserve(entry.target);
            });
          },
          phone.matches ? { root: track, rootMargin: '0px 10% 0px 0px' } : { rootMargin: '100% 0px 100% 0px' }
        );
        waiting.filter((v) => !v.dataset.mmWarm).forEach((v) => observer.observe(v));
      };
      watch();
      if (phone.addEventListener) phone.addEventListener('change', watch);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// 6. Variant change -----------------------------------------------------------------------------
// Dawn swaps price and buttons when a size is chosen, but not the Mirayama notes. Here the parts
// marked data-mm-sync (Availability row, trust line, the fold's made-to-order row) are copied from
// the section Dawn has just fetched for the new variant, and the phone buy bar takes the new
// price and the state of the main button ("Sold out" when that size is gone).
(function () {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const data = event && event.data;
    if (!data || !data.html || !data.sectionId) return;
    const root = document.getElementById(`MainProduct-${data.sectionId}`) || document;
    root.querySelectorAll('[data-mm-sync]').forEach((el) => {
      const fresh = data.html.querySelector(`[data-mm-sync="${el.dataset.mmSync}"]`);
      if (!fresh) return;
      el.innerHTML = fresh.innerHTML;
      el.hidden = fresh.hasAttribute('hidden');
    });

    const bar = document.querySelector(`mm-buy-bar[data-section="${data.sectionId}"]`);
    if (!bar) return;
    const freshPrice = data.html.querySelector('.mm-buy-bar__price');
    const price = bar.querySelector('.mm-buy-bar__price');
    if (freshPrice && price) price.textContent = freshPrice.textContent;
    const add = bar.querySelector('[data-mm-buy-bar-add]');
    const main = data.html.getElementById(`ProductSubmitButton-${data.sectionId}`);
    if (add) {
      const soldOut = !main || main.hasAttribute('disabled');
      add.disabled = soldOut;
      add.textContent = soldOut ? window.variantStrings?.soldOut || 'Sold out' : window.variantStrings?.addToCart || 'Add to cart';
    }
  });
})();
